// shared/auth-failure.ts — is this auth error worth refreshing the token for?
//
// LOBS returns 403 — not 401 — when an access token has expired: its auth check
// surfaces the UACS /verify failure as UserAuthorizationException, which
// ControllerFactory maps to 403. Access tokens live 20 minutes, so a 401-only
// refresh rule strands users mid-session. In one week of prod traffic that was
// ~5,300 auth errors that a silent refresh would have absorbed.
//
// The catch is that 403 is overloaded. The same status also means "your role
// may not do this" — e.g. FileUploadV2Controller rejecting a residence-
// verification upload from a role outside its allow-list. Refreshing on those
// is pointless, and if the refresh failed we would log out a user who merely
// lacked a permission. So match the token-expiry bodies specifically instead of
// treating every 403 as refreshable.
//
// Keep this in sync with the messages thrown by LOBS UserAccessServiceAccessor.

const EXPIRED_TOKEN_BODY =
  /invalid access token|authorization check failed|failed to verify user authorization/i;

/** Pull the response body out as text, whether the backend sent plain text
 *  (LOBS today) or JSON (if that ever changes). */
function bodyText(data: unknown): string {
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    return String(obj.message ?? obj.error ?? obj.errorMessage ?? '');
  }
  return '';
}

/** True when the status/body indicate an expired token rather than a genuine
 *  permission denial — i.e. a token refresh could plausibly fix it. */
export function isRefreshableAuthFailure(status: number | undefined, data: unknown): boolean {
  if (status === 401) return true;
  if (status !== 403) return false;
  return EXPIRED_TOKEN_BODY.test(bodyText(data));
}
